<script setup lang="ts">
import { computed } from 'vue';
import SuggestionTextArea from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/SuggestionTextArea/SuggestionTextArea.vue';
import type { CopilotConfigurationFormData } from '@/components/CopilotPersonalities/types/copilot-configuration-template.interface';

const props = defineProps<{
  modelValue: CopilotConfigurationFormData;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: CopilotConfigurationFormData];
}>();

const productsAndServices = computed({
  get: () => props.modelValue?.productsAndServices || '',
  set: (value: string) => {
    if (!props.modelValue) return;
    emit('update:modelValue', {
      ...props.modelValue,
      productsAndServices: value,
    });
  },
});

const competitorsAndDifferentiation = computed({
  get: () => props.modelValue?.competitorsAndDifferentiation || '',
  set: (value: string) => {
    if (!props.modelValue) return;
    emit('update:modelValue', {
      ...props.modelValue,
      competitorsAndDifferentiation: value,
    });
  },
});

const employerDescription = computed({
  get: () => props.modelValue?.employerDescription || '',
  set: (value: string) => {
    if (!props.modelValue) return;
    emit('update:modelValue', {
      ...props.modelValue,
      employerDescription: value,
    });
  },
});

const employeeBenefits = computed({
  get: () => props.modelValue?.employeeBenefits || '',
  set: (value: string) => {
    if (!props.modelValue) return;
    emit('update:modelValue', {
      ...props.modelValue,
      employeeBenefits: value,
    });
  },
});

const employerTraditionsAndMission = computed({
  get: () => props.modelValue?.employerTraditionsAndMission || '',
  set: (value: string) => {
    if (!props.modelValue) return;
    emit('update:modelValue', {
      ...props.modelValue,
      employerTraditionsAndMission: value,
    });
  },
});
</script>

<template>
  <div v-if="modelValue" class="flex flex-col gap-6">
    <SuggestionTextArea
      v-model="productsAndServices"
      :title="'What do you build or offer, and which industries or customers do you serve?'"
      title-class="!text-base !font-extrabold"
      :placeholder="'e.g., We provide software solutions for healthcare providers, helping them manage patient data and streamline operations...'"
      optional
    />

    <SuggestionTextArea
      v-model="competitorsAndDifferentiation"
      :title="'Do you compete with similar companies? If so, who are your competitors and how are you different?'"
      title-class="!text-base !font-extrabold"
      :placeholder="'e.g., Yes, we compete with companies like X, Y, and Z. What sets us apart is our focus on user experience and our proprietary technology that enables faster implementation...'"
      optional
    />

    <SuggestionTextArea
      v-model="employerDescription"
      :title="'In a few sentences, how would you describe your company to a candidate?'"
      title-class="!text-base !font-extrabold"
      :placeholder="'e.g., Our company is a leader in [industry] dedicated to [mission]. We focus on innovation and collaboration, providing our team members with opportunities to grow while making a meaningful impact...'"
      optional
    />

    <SuggestionTextArea
      v-model="employeeBenefits"
      :title="'What do employees love about working for you (culture, benefits, growth)?'"
      title-class="!text-base !font-extrabold"
      :placeholder="'e.g., Our employees appreciate our commitment to safety, opportunities for skill development, and our team-oriented environment on the shop floor. We offer competitive benefits, regular training, and celebrate production milestones together…'"
      optional
    />

    <SuggestionTextArea
      v-model="employerTraditionsAndMission"
      :title="'Anything else that sets your company apart? Fun traditions, mission statement, etc.'"
      title-class="!text-base !font-extrabold"
      :placeholder="'e.g., We have a strong safety culture and celebrate production milestones with team lunches. Our mission is to deliver quality products on time, and we host annual family days and recognize long-term employees for their dedication…'"
      optional
    />
  </div>
</template>

<style lang="scss" scoped>
.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;

  .v-combobox__selection {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }

  .v-field__outline {
    @apply text-tint-80;
  }
  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }
  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }
  }
}

.v-input :deep(.v-field.v-field--focused),
.v-input--horizontal :deep(.v-field.v-field--focused) {
  .v-field__outline {
    @apply text-highlight-500;
  }
}

.v-input :deep(.v-field.v-field--error),
.v-input--horizontal :deep(.v-field.v-field--error) {
  .v-field__outline {
    @apply text-critical-500;
  }
}
</style>
